import { Effect, Schema } from 'effect';

export const UserFeatureFlagSchema = Schema.Literals([
  'googleDrive',
  'dropbox',
  'conversionV2',
]);
export type UserFeatureFlag = typeof UserFeatureFlagSchema.Type;

export const UserFeatureFlags = Schema.Struct({
  googleDrive: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
  googleDriveWeb: Schema.optionalKey(Schema.Boolean),
  dropbox: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
  cloudImports: Schema.Boolean,
});
export type UserFeatureFlags = typeof UserFeatureFlags.Type;

type FlagDefinition = {
  readonly label: string;
  readonly description: string;
  readonly enabledByDefault: (mode: string) => boolean;
};

const enabledOutsideProduction = (mode: string) => mode !== 'production';

export const USER_FEATURE_FLAGS = {
  googleDrive: {
    label: 'Google Drive',
    description:
      'Connect and import from Google Drive. The web app has it unless blocked. In production the mobile apps only show it to accounts set to Allow; elsewhere it is on unless blocked.',
    enabledByDefault: enabledOutsideProduction,
  },
  dropbox: {
    label: 'Dropbox',
    description:
      'Connect and import from Dropbox. Allow and Block override the default. Production web access follows the dropbox-web-v1 PostHog percentage rollout, capped at 50 completed connections with temporary reservations during OAuth. Connected pilot users keep web access. Mobile remains Allow-only in production; other stages are on unless blocked.',
    enabledByDefault: enabledOutsideProduction,
  },
  conversionV2: {
    label: 'Conversion pipeline v2',
    description:
      'New uploads convert on pipeline v2 (Paddle OCR) and build a doc-pack. Documents already converted keep the pipeline they ran on. Allow and Block always win. Otherwise staging and development are on, production follows the share of users the PostHog flag conversion-pipeline-v2 rolls out to, and other stages are off.',
    enabledByDefault: (mode) => mode === 'staging' || mode === 'development',
  },
} as const satisfies Record<UserFeatureFlag, FlagDefinition>;

export const userFeatureFlagOverrides = (
  rows: ReadonlyArray<{ readonly flag: string; readonly enabled: boolean }>,
) =>
  new Map(
    rows.flatMap((row) =>
      Schema.is(UserFeatureFlagSchema)(row.flag)
        ? [[row.flag, row.enabled] as const]
        : [],
    ),
  );

export type ImportFeatureFlag = Exclude<UserFeatureFlag, 'conversionV2'>;
export type ImportAccessFlag = Exclude<keyof UserFeatureFlags, 'cloudImports'>;

export const resolveUserFeatureFlags = (
  mode: string,
  overrides: ReadonlyMap<UserFeatureFlag, boolean>,
) => {
  const enabled = (flag: ImportFeatureFlag) =>
    overrides.get(flag) ?? USER_FEATURE_FLAGS[flag].enabledByDefault(mode);
  const googleDrive = enabled('googleDrive');
  const googleDriveWeb = overrides.get('googleDrive') ?? true;
  const dropbox = enabled('dropbox');
  return {
    googleDrive,
    googleDriveWeb,
    dropbox,
    cloudImports: googleDrive && dropbox,
  };
};
